/**
 * Lightning Arena — real Lightning integration.
 * Talks HTTP REST to the bolt12-server behind https://bolt.21pay.org:
 *   POST /api/v1/arena/join       → entry invoice (BOLT11) + session token
 *   GET  /api/v1/arena/status/:t  → paid? frags credited? pool balance
 *   POST /api/v1/arena/kill       → report a frag (credits bounty)
 *   POST /api/v1/arena/withdraw   → pay bounty to the player's BOLT11 invoice
 * No API key in the browser: protection is the HMAC session token + caps.
 */

export interface ArenaJoin {
  session_token: string;
  payment_request: string;
  invoice_hash: string;
  entry_sats: number;
  bounty_sats: number;
  max_frags: number;
}

export interface ArenaStatus {
  paid: boolean;
  frags: number;
  bounty_sats: number;
  credit: number;
  pool: number;
  entry_sats: number;
  max_frags: number;
}

export interface ArenaKillResult {
  frags: number;
  credit: number;
  bounty_sats: number;
  pool: number;
}

export interface ArenaWithdrawResult {
  lnurl: string | null;
  lnurl_url: string | null;
  claim_id: string;
  amount_sats: number;
  claimed: boolean;
  credit: number;
  pool: number;
}

export interface ArenaWithdrawStatus {
  claimed: boolean;
  credit: number;
  pool: number;
}

export class ArenaClient {
  private base = 'https://bolt.21pay.org/api/v1/arena';
  public available = false;
  public entrySats = 2100;
  public bountySats = 600;
  public maxFrags = 3;

  constructor(base?: string) {
    if (base) this.base = base.replace(/\/$/, '') + '/arena';
  }

  baseUrl(): string {
    return this.base.replace(/\/api\/v1\/arena$/, '');
  }

  private async post(path: string, body?: Record<string, unknown>, timeoutMs = 15000): Promise<Record<string, unknown>> {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const r = await fetch(this.base + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
        signal: ctrl.signal,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error((data as { error?: string }).error || `HTTP ${r.status}`);
      }
      return data as Record<string, unknown>;
    } finally {
      clearTimeout(t);
    }
  }

  private async get(path: string, timeoutMs = 15000): Promise<Record<string, unknown>> {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const r = await fetch(this.base + path, { signal: ctrl.signal });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error((data as { error?: string }).error || `HTTP ${r.status}`);
      }
      return data as Record<string, unknown>;
    } finally {
      clearTimeout(t);
    }
  }

  /** Health probe — sets this.available. Never throws. */
  async probe(): Promise<boolean> {
    const healthUrl = this.base + '/health';
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 6000);
      const r = await fetch(healthUrl, { signal: ctrl.signal });
      clearTimeout(t);
      this.available = r.ok;
      if (r.ok) {
        try {
          const d = (await r.json()) as { data?: { entry_sats?: number; bounty_sats?: number; max_frags?: number } };
          if (d.data) {
            if (typeof d.data.entry_sats === 'number') this.entrySats = d.data.entry_sats;
            if (typeof d.data.bounty_sats === 'number') this.bountySats = d.data.bounty_sats;
            if (typeof d.data.max_frags === 'number') this.maxFrags = d.data.max_frags;
          }
        } catch { /* keep defaults */ }
      }
    } catch {
      this.available = false;
    }
    return this.available;
  }

  async join(): Promise<ArenaJoin> {
    const r = await this.post('/join');
    const d = (r as { data?: ArenaJoin }).data;
    if (!d) throw new Error('join: missing data');
    return d;
  }

  async status(token: string): Promise<ArenaStatus> {
    const r = await this.get('/status/' + encodeURIComponent(token));
    const d = (r as { data?: ArenaStatus }).data;
    if (!d) throw new Error('status: missing data');
    return d;
  }

  async kill(token: string, matchId: string): Promise<ArenaKillResult> {
    const r = await this.post('/kill', { session_token: token, match_id: matchId });
    const d = (r as { data?: ArenaKillResult }).data;
    if (!d) throw new Error('kill: missing data');
    return d;
  }

  async withdraw(token: string): Promise<ArenaWithdrawResult> {
    const r = await this.post('/withdraw', { session_token: token });
    const d = (r as { data?: ArenaWithdrawResult }).data;
    if (!d) throw new Error('withdraw: missing data');
    return d;
  }

  async withdrawStatus(token: string): Promise<ArenaWithdrawStatus> {
    const r = await this.get('/withdraw/status/' + encodeURIComponent(token));
    const d = (r as { data?: ArenaWithdrawStatus }).data;
    if (!d) throw new Error('withdraw/status: missing data');
    return d;
  }

  /** URL of the QR PNG — proxied through the same origin (CSP-safe). */
  withdrawQrUrl(token: string): string {
    return '/jeux/lightning-arena/qr/withdraw/' + encodeURIComponent(token);
  }

  invoiceQrUrl(token: string): string {
    return '/jeux/lightning-arena/qr/entry/' + encodeURIComponent(token);
  }
}
